import React, { useEffect, useState } from 'react';
import styles from '../../styles/components/question-flow/QuestionFlow.module.css';
import Stepper from '@ax2/common/components/Stepper';
import {
  getQuestionBuilder,
  QuestionBuilder,
} from '../../helpers/questionsBuilder';
import Progress from '@ax2/common/components/Progress';
import { percentage } from '../../helpers/math';
import AnswerData from '@ax2/common/model/answers-data/AnswerData';
import QuestionFlowHeader from './QuestionFlowHeader';
import UUID from '@ax2/common/model/UUID';
import { ActionButton } from '@ax2/common/components/ActionButton';
import QuestionSchema from '@ax2/common/model/schemas/QuestionSchema';
import QuestionType from '@ax2/common/model/QuestionType';
import _ from 'lodash';
import ScreenerQuestionSchema from '@ax2/common/model/schemas/ScreenerQuestionSchema';
import { isTerminated } from '../../helpers/screeners';
import useMediaQuery from '@ax2/common/helpers/hooks/useMediaQuery';
import breakpoints from '@ax2/common/styles/Breakpoints.module.css';
import { useErrorUpdateContext } from '@ax2/common/contexts/ErrorContext';
import { getErrorMessage } from '@ax2/common/helpers/error';
import { RestError } from '@ax2/common/rest/baseClient';

export interface TitleText {
  title: string;
  isSubtitle: boolean;
}

export interface FinalStep {
  name: string;
  content?: (doBack: () => void) => React.ReactNode;
}

export interface QuestionFlowProps {
  screeners: ScreenerQuestionSchema[];
  questions: QuestionSchema[];
  preselectedAnswers?: AnswerData[];
  header?: TitleText;
  activeStepNumber?: number;
  finalStep: FinalStep;
  hasSkip?: boolean;
  isFastPreview?: boolean;
  onStepChange?: (nextStep: number) => void;
  onStepComplete?: (data: AnswerState) => void;
  onTerminate?: () => void;
  setPreviewColor?: () => void;
  checkAllStepColplete?: () => void;
}

type AnswerState = AnswerData | undefined;

interface StepContent {
  builder: QuestionBuilder;
  question: ScreenerQuestionSchema | QuestionSchema;
  isScreener: boolean;
}

const getPreviousSelectionState = (
  questions: QuestionSchema[],
  previousState?: AnswerData[]
): AnswerState[] => {
  return _.map(questions, question =>
    _.find(previousState, ps => ps.questionId === question.id)
  );
};

const QuestionFlow: React.FC<QuestionFlowProps> = ({
  screeners,
  questions,
  preselectedAnswers,
  header,
  activeStepNumber,
  finalStep,
  hasSkip = false,
  isFastPreview = false,
  onStepChange,
  onStepComplete,
  onTerminate,
  setPreviewColor,
  checkAllStepColplete,
}) => {
  const updateError = useErrorUpdateContext();
  const [stepsContent, setStepsContent] = React.useState<StepContent[]>([]);
  const [inProgress, setInProgress] = useState(false);
  const [step, setStep] = React.useState(0);
  const [qData, setQdata] = React.useState<AnswerState[]>([]);
  const [isReqErrorVisible, setIsReqErrorVisible] = React.useState(false);
  const stepContent = step < stepsContent.length ? stepsContent[step] : null;
  const builder = stepContent ? stepContent.builder : null;
  const stepData = qData[step];
  const reqValError = builder ? builder.getRequiredValidation(stepData) : null;
  const isCompleteVisible = step === stepsContent.length;
  const isLgBreakpoint = useMediaQuery(breakpoints.lg);
  const isDesktop = isLgBreakpoint !== false;

  const [questionsData, setQuestionsData] = React.useState<
    {
      q: QuestionSchema;
      isScreener: boolean;
    }[]
  >([]);

  useEffect(() => {
    // Compute questions data
    const sData = _.map(screeners, s => ({
      q: s,
      isScreener: true,
    }));
    const qData = _.map(questions, s => ({
      q: s,
      isScreener: false,
    }));
    const data = _.filter(
      _.concat(sData, qData),
      question => question.q.questionType !== QuestionType.Empty
    );
    setQuestionsData(data);
  }, [screeners, questions]);

  useEffect(() => {
    // Compute questions content
    const sLength = _.filter(questionsData, d => d.isScreener).length;
    const stepsContent = _.map(questionsData, (d, i) => {
      const question = isFastPreview ? { ...d.q, isRequired: false } : d.q;
      const number =
        (d.isScreener ? 'S' : 'Q') + (1 + (d.isScreener ? i : i - sLength));
      return {
        builder: getQuestionBuilder(question, isDesktop, number),
        question: question,
        isScreener: d.isScreener,
      };
    });
    setStepsContent(stepsContent);
  }, [questionsData, preselectedAnswers, isFastPreview, isDesktop]);

  useEffect(() => {
    // Compute answers
    const allQuestions = _.map(questionsData, c => c.q);
    setQdata(getPreviousSelectionState(allQuestions, preselectedAnswers));
  }, [questionsData, preselectedAnswers]);

  useEffect(() => {
    // Compute selection state of the component
    setStep(activeStepNumber !== undefined ? activeStepNumber : 0);
  }, [activeStepNumber]);

  return stepsContent.length > 0 ? (
    <div className={styles.container}>
      <div className={styles.header}>
        {header && (
          <QuestionFlowHeader
            title={header.title}
            subtitle={
              stepsContent.length > step
                ? stepsContent[step].isScreener
                  ? `Screener ${step + 1}`
                  : `Question ${step + 1}`
                : null
            }
          />
        )}
      </div>
      <div className={styles.content}>
        {/* Stepper */}
        <div className={styles.stepperWrapper}>
          <Stepper
            labels={getStepperLabels(stepsContent, finalStep)}
            step={step}
            onCompletion={s => {
              if (s < step) {
                setStep(s);
                onStepChange && onStepChange(s);
              }
            }}
          />
        </div>
        {/* Progress Bar */}
        {!isCompleteVisible && (
          <div className={styles.progressWrapper}>
            <Progress
              percentage={
                stepsContent.length
                  ? percentage(
                      stepsContent.filter((_q, i) => i < step).length,
                      stepsContent.length
                    )
                  : 0
              }
            />
          </div>
        )}
        {/* Submit */}
        {isCompleteVisible && (
          <div className={styles.completeWrapper}>
            {finalStep.content &&
              finalStep.content(() => {
                setStep(step - 1);
                onStepChange && onStepChange(step - 1);
              })}
          </div>
        )}
        {/* Question Flow */}
        {!isCompleteVisible && builder && (
          <div className={styles.surveyWrapper}>
            <div className={styles.questionContainer}>
              {builder &&
                builder.getQuestion(stepData, d => {
                  const newData = [...qData];
                  newData[step] = d;
                  setQdata(newData);
                })}
            </div>
            {isReqErrorVisible && reqValError && (
              <div className={styles.errorsContainer}>
                <span className={styles.errorText}>{reqValError}</span>
              </div>
            )}
            <div className={styles.actionsContainer}>
              <div className={styles.btnBack}>
                <ActionButton
                  tabIndex={1001}
                  text="Back"
                  type="secondary"
                  disabled={step === 0}
                  onClick={() => {
                    if (step > 0) {
                      setStep(step - 1);
                      onStepChange && onStepChange(step - 1);
                      setIsReqErrorVisible(false);
                    }
                  }}
                />
              </div>
              <div className={styles.btnNext}>
                {(step + 1 < stepsContent.length || finalStep.content) && (
                  <ActionButton
                    tabIndex={1000}
                    loading={inProgress}
                    text={isCompleteVisible ? finalStep.name : 'NEXT QUESTION'}
                    type="primary"
                    disabled={isReqErrorVisible && !!reqValError}
                    onClick={async () => {
                      if (reqValError === null && inProgress === false) {
                        setIsReqErrorVisible(false);

                        if (
                          onTerminate &&
                          stepData !== undefined &&
                          stepContent &&
                          stepContent.isScreener &&
                          isTerminated(
                            stepContent.question as ScreenerQuestionSchema,
                            stepData
                          )
                        ) {
                          await onTerminate();
                          return; // we don't need to update component after termination
                        }

                        if (
                          step == stepsContent.length - 1 &&
                          checkAllStepColplete
                        ) {
                          checkAllStepColplete();
                        }
                        if (step !== stepsContent.length && onStepComplete) {
                          setInProgress(true);
                          try {
                            await onStepComplete(stepData);
                          } catch (e) {
                            updateError({
                              text: getErrorMessage(e as RestError),
                            });
                            setInProgress(false);
                            return; // we don't need to update component after error
                          }
                          setInProgress(false);
                        }
                        if (
                          step + 1 === stepsContent.length &&
                          finalStep.content === undefined
                        ) {
                          //If we don't have final step we cannot get on it.
                          return;
                        }
                        setStep(step + 1);
                        onStepChange && onStepChange(step + 1);
                      } else {
                        setIsReqErrorVisible(true);
                      }
                    }}
                  />
                )}
              </div>
              {hasSkip && (
                <div className={styles.btnSkip}>
                  <ActionButton
                    tabIndex={999}
                    text={'SKIP TO ' + finalStep.name}
                    type="secondaryAlt"
                    onClick={() => {
                      setStep(stepsContent.length);
                      if (setPreviewColor) {
                        setPreviewColor();
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  ) : (
    <div></div>
  );
};

export default QuestionFlow;

export const getFlowStep = (
  screeners: QuestionSchema[],
  questions: QuestionSchema[],
  activeQuestionId: UUID
) => {
  const allQuestions = _.filter(
    _.concat(screeners, questions),
    q => q.questionType !== QuestionType.Empty
  );
  return _.findIndex(allQuestions, q => q.id === activeQuestionId);
};

const getStepperLabels = (
  stepsContent: StepContent[],
  finalStep: FinalStep
) => {
  const screenersLabels = _.map(
    _.filter(stepsContent, sc => sc.isScreener),
    (_c, i) => `S${i + 1}`
  );
  const questionsLabels = _.map(
    _.filter(stepsContent, sc => !sc.isScreener),
    (_c, i) => `Q${i + 1}`
  );
  return screenersLabels.concat(questionsLabels).concat(finalStep.name);
};
